import React from 'react';

import { cssMap } from '@compiled/react';
import { token } from '@atlaskit/tokens';

import ButtonGroup from '@atlaskit/button/button-group';
import Button, { IconButton } from '@atlaskit/button/new';
import Form, {
	ErrorMessage,
	Field,
	FormFooter,
	FormHeader,
	FormSection,
	HelperMessage,
	RequiredAsterisk,
} from '@atlaskit/form';

import CopyIcon from '@atlaskit/icon/core/copy';

import { Text, Stack, Inline, Box } from '@atlaskit/primitives/compiled';
import Link from '@atlaskit/link';
import Textfield from '@atlaskit/textfield';
import { CheckboxSelect, type ValueType } from '@atlaskit/select';
import { useIntl } from '@atlassian/jira-intl';

import type { SelectOption, StageConnectProps, StageProps } from '../../../../common/types.tsx';
import type { ApplicationLinkRemoteOAuth2ApplicationLink } from '../../../../common/entities.tsx';

import { putOAuth2ConfigInfo } from '../../../../rest/index.tsx';
import { getApplinksAdminUrl } from '../../../../common/interchange.tsx';
import { APPLINKS_LIST_PAGE_URL } from '../../../../common/constants.tsx';

const styles = cssMap({
	linkDisplay: {
		marginTop: token('space.200'),
		marginBottom: token('space.200'),
	},
	growField: {
		flexGrow: 1,
	},
});

const CopyableApplicationClientID = (props: { clientId: string }) => (
	<Field
		aria-required
		name="copyableApplicationClientId"
		defaultValue={props.clientId}
		label="Application Client ID"
		isDisabled
	>
		{({ fieldProps }) => (
			<Textfield
				isReadOnly
				{...fieldProps}
				elemAfterInput={
					<IconButton
						appearance="subtle"
						icon={CopyIcon}
						label="Copy Application Client ID"
						isTooltipDisabled={false}
					/>
				}
			/>
		)}
	</Field>
);

const CopyableApplicationClientSecret = (props: { clientSecret: string }) => (
	<Field
		aria-required
		name="copyableApplicationClientSecret"
		defaultValue={props.clientSecret}
		label="Application Client secret"
		isDisabled
	>
		{({ fieldProps }) => (
			<Textfield
				isReadOnly
				{...fieldProps}
				elemAfterInput={
					<IconButton
						appearance="subtle"
						icon={CopyIcon}
						label="Copy Application Client secret"
						isTooltipDisabled={false}
					/>
				}
			/>
		)}
	</Field>
);

const RemoteApplicationScopeMultiSelect = (props: { supportedScopes: string[] }) => (
	<Field<ValueType<SelectOption, true>> name="remoteApplicationScopes" label="Scopes">
		{({ fieldProps }) => (
			<CheckboxSelect
				placeholder="Use the same scopes as the remote application"
				{...fieldProps}
				options={props.supportedScopes.map((scope) => ({
					label: scope,
					value: scope,
				}))}
			/>
		)}
	</Field>
);

const ApplicationClientId = () => (
	<Field
		aria-required
		name="applicationClientId"
		defaultValue=""
		label="Application Client ID"
		isRequired
	>
		{({ fieldProps }) => <Textfield placeholder="Client ID" {...fieldProps} />}
	</Field>
);

const ApplicationClientSecret = () => (
	<Field
		aria-required
		name="applicationClientSecret"
		defaultValue=""
		label="Application Client secret"
		isRequired
	>
		{({ fieldProps }) => <Textfield placeholder="Client secret" {...fieldProps} />}
	</Field>
);

const RedirectUrl = () => (
	<Field aria-required name="redirectUrl" defaultValue="" label="Redirect URL" isRequired>
		{({ fieldProps, error }) => (
			<>
				<Textfield placeholder="Redirect URL" {...fieldProps} />
				{error && <ErrorMessage>{error}</ErrorMessage>}
			</>
		)}
	</Field>
);

const DisplayName = (props: { defaultName: string }) => (
	<Field aria-required name="displayName" defaultValue={props.defaultName} label="Display name">
		{({ fieldProps }) => (
			<Stack>
				<Textfield placeholder="Name for the application link" {...fieldProps} />
				<HelperMessage>
					Enter a unique name for the link, for example the name of your external application.
				</HelperMessage>
			</Stack>
		)}
	</Field>
);

type ConnectSubpageProps = {
	state: StageConnectProps;
	onSuccess: (newState: StageProps) => void;
	onError: (error: string) => void;
};

const ConnectSubpage = (props: ConnectSubpageProps) => {
	useIntl();

	const onFormSubmit = async (data: {
		remoteApplicationSupportedScopes: ValueType<SelectOption, true>;
		applicationClientId: string;
		applicationClientSecret: string;
		redirectUrl?: string;
		displayName: string;
	}) => {
		// TODO: Validate the scopes before proceeding
		const scopes = data.remoteApplicationSupportedScopes
			? data.remoteApplicationSupportedScopes.map((scope) => scope.value)
			: [];

		const oauth2Config: ApplicationLinkRemoteOAuth2ApplicationLink = {
			remoteClientId: data.applicationClientId,
			remoteClientSecret: data.applicationClientSecret,
			remoteRedirectUrl: data.redirectUrl,

			outboundScopes: scopes,
		};

		try {
			await putOAuth2ConfigInfo(props.state.remoteManifest.id, oauth2Config);

			// TODO: Do a single page app transition back to list
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.location.href = APPLINKS_LIST_PAGE_URL;
		} catch (error) {
			if (error instanceof Error) {
				props.onError(error.message);
			} else {
				props.onError(
					'An unknown error occurred while configuring the OAuth 2.0 application link.',
				);
			}
			return error;
		}
	};

	return (
		<Form<{
			remoteApplicationSupportedScopes: ValueType<SelectOption, true>;
			applicationClientId: string;
			applicationClientSecret: string;
			redirectUrl?: string;
			displayName: string;
		}>
			onSubmit={onFormSubmit}
		>
			{({ formProps, submitting }) => (
				<form {...formProps}>
					<Stack space="space.200">
						<FormHeader title="Connect to the application">
							<Stack space="space.200">
								<Text>
									Copy and paste the details below between this page and the settings page of the
									external application where you configure application links. This creates a secure
									connection.
								</Text>
							</Stack>

							<Text aria-hidden="true" size="small">
								Required fields are marked with an asterisk <RequiredAsterisk />
							</Text>
						</FormHeader>

						<FormSection
							title="Outgoing link"
							description="Copy these details to the external application. If you're not an admin of the application, ask an existing admin to make the updates for you."
						>
							<Stack space="space.200">
								<Inline space="space.200">
									<Box xcss={styles.growField}>
										<CopyableApplicationClientID clientId={props.state.localClientId} />
									</Box>
									<Box xcss={styles.growField}>
										<CopyableApplicationClientSecret clientSecret={props.state.localClientSecret} />
									</Box>
								</Inline>
								<RemoteApplicationScopeMultiSelect
									supportedScopes={props.state.remoteApplicationSupportedScopes}
								/>
								<Link
									href={getApplinksAdminUrl(
										props.state.remoteManifest.url,
										props.state.remoteManifest.typeId,
									)}
									target="_blank"
								>
									Go to {props.state.remoteManifest.url}
								</Link>
							</Stack>
						</FormSection>

						<FormSection
							title="Incoming link"
							description="Find these details in the external application and enter them below. You may need to ask an admin of the external application for these details."
						>
							<Stack space="space.200">
								<Inline space="space.200">
									<Box xcss={styles.growField}>
										<ApplicationClientId />
									</Box>
									<Box xcss={styles.growField}>
										<ApplicationClientSecret />
									</Box>
								</Inline>
								{/* Only show for non-cloud links - we already know the Cloud redirect URL */}
								{!props.state.remoteManifest.isCloud && <RedirectUrl />}
								<DisplayName defaultName={props.state.remoteManifest.name} />
							</Stack>
						</FormSection>

						<FormFooter>
							<ButtonGroup label="Form submit options">
								<Button appearance="subtle">Save and exit</Button>
								<Button type="submit" appearance="primary" isLoading={submitting}>
									Connect
								</Button>
							</ButtonGroup>
						</FormFooter>
					</Stack>
				</form>
			)}
		</Form>
	);
};

export default ConnectSubpage;
